<script setup>
import { reactive, ref, onMounted, computed, watch } from 'vue';
import { useEventsStore } from '@/stores/events';
import { useAuthStore } from '@/stores/auth';

const eventsStore = useEventsStore();
const authStore = useAuthStore();

// Preload events from #upcoming-events div
const container = document.getElementById('upcoming-events');
const rawData = container?.dataset?.events ?? '[]';

let initialEvents = [];

try {
	initialEvents = JSON.parse(rawData);
} catch (error) {
	console.error('Failed to parse preloaded events JSON:', error);
}

const fetched = computed(() => {
	return eventsStore.eventsFetched;
});

const events = computed(() => {
	if (fetched.value) {
		return eventsStore.upcomingEvents;
	}

	return initialEvents.length ? initialEvents : [];
});

const moreEvents = computed(() => {
	return events.value.length > 1 ? events.value.slice(1) : [];
});

const fetchEvents = async() => {
	try {
		const response = await axios.get(window.portal.events_url);

		eventsStore.setUpcomingEvents(response.data);
	}
	catch (error) {

	} finally {

	}
}

//Fetch every time and update store
fetchEvents();

//Fetch events again if the logged in status changes
watch(() => authStore.isLoggedIn, (newVal, oldVal) => {
    fetchEvents();
});

const portal = ref(window.portal);
const labels = ref(window.portal.labels);
const placeholder = ref(window.portal.placeholder);

</script>

<template>
	<div class="wp-block-group alignfull has-fc-black-background-color is-layout-constrained" style="padding-left: var(--wp--preset--spacing--50); padding-right: var(--wp--preset--spacing--50); padding-bottom: var(--wp--preset--spacing--60); padding-top: calc(var(--wp--preset--spacing--60) + 1rem); border-bottom-left-radius: 1rem; border-bottom-right-radius: 1rem; margin-top: -1rem; z-index: 0;">
		<div class="wp-block-group alignwide is-layout-flow">
			<h2 class="is-style-eyebrow has-text-color has-fc-green-400-color" v-html="labels.upcoming_events"></h2>

			<div class="wp-block-columns" style="margin-top: var(--wp--preset--spacing--60); gap: var(--wp--preset--spacing--50);" v-if="!events.length">
				<div class="wp-block-column" style="flex-basis: 33%;">
					<figure class="wp-block-image">
						<img :src="placeholder"/>
					</figure>
				</div>

				<div class="wp-block-column is-layout-flow" style="flex-basis: 67%;">
					<h3 class="has-text-color has-fc-green-400-color has-h-4-font-size" v-html="labels.no_upcoming_events"></h3>

					<p class="has-text-color has-white-color" v-html="labels.no_events_scheduled"></p>

					<div class="wp-block-buttons">
						<div class="wp-block-button">
							<a class="wp-block-button__link" :href="portal.internal_links.news_url" v-html="labels.sign_up_for_news"></a>
						</div>
					</div>
				</div>
			</div>

			<div class="wp-block-columns" style="margin-top: var(--wp--preset--spacing--60); gap: var(--wp--preset--spacing--50);" v-else>
				<div class="wp-block-column" style="flex-basis: 33%;">
					<figure class="wp-block-image">
						<img :src="events[0].image_uri ? events[0].image_uri : placeholder"/>
					</figure>
				</div>

				<div class="wp-block-column is-layout-flow" style="flex-basis: 67%;">
					<h3 class="has-text-color has-fc-green-400-color has-h-4-font-size" v-html="events[0].name"></h3>

					<p class="has-text-color has-white-color" v-if="events[0].teaser" v-html="events[0].teaser"></p>

					<p class="has-text-color has-admin-grey-200-color has-small-font-size">
						<strong v-html="labels.date_time"></strong><br/>
						<span v-html="events[0].date_range"></span>
					</p>

					<p class="has-text-color has-admin-grey-200-color has-small-font-size" v-if="events[0].location">
						<strong v-html="labels.location"></strong><br/>
						<a class="event-link" style="color: var(--wp--preset--color--admin-grey-200);" v-if="events[0].location.url" :href="events[0].location.url" target="_blank" rel="noopener nofollow" v-html="events[0].location.name"></a>
						<span v-else v-html="events[0].location.name"></span>
						<template v-if="events[0].location.directions_url">
							<span>&nbsp;|&nbsp;<a class="event-link" style="color: var(--wp--preset--color--admin-grey-200);" :href="events[0].location.directions_url" target="_blank" rel="noopener nofollow" v-html="labels.directions"></a></span>
						</template>
					</p>

					<div class="wp-block-buttons">
						<div class="wp-block-button">
							<a class="wp-block-button__link" :href="eventsStore.getEventUrl(events[0].key)" v-html="labels.learn_more"></a>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>

	<div class="wp-block-group alignfull is-layout-constrained" style="margin-top: var(--wp--preset--spacing--70); margin-bottom: var(--wp--preset--spacing--70); padding-left: var(--wp--preset--spacing--50); padding-right: var(--wp--preset--spacing--50);" v-if="fetched && moreEvents.length">
		<div class="wp-block-query alignwide is-layout-flow">
			<ul class="is-layout-grid columns-3 wp-block-post-template">
				<li class="wp-block-post" v-for="event in moreEvents">
					<div class="wp-block-group is-layout-flex is-vertical" style="box-shadow: var(--wp--custom--shadow--ii); border-radius: 1rem; gap: 0; height: 100%;">
						<figure class="wp-block-image" style="margin-bottom: 0;">
							<img :src="event.image_uri ? event.image_uri : placeholder" style="border-bottom-left-radius: 0; border-bottom-right-radius: 0; aspect-ratio: 458 / 183;"/>
						</figure>

						<div class="wp-block-group is-layout-flow" style="padding: var(--wp--preset--spacing--30); border: solid 1px var(--wp--preset--color--admin-grey-200); border-top: none; border-bottom-left-radius: inherit; border-bottom-right-radius: inherit; flex-grow: 1; width: 100%;">
							<h3 class="has-medium-font-size" v-html="event.name"></h3>

							<p v-if="event.teaser" class="has-xsmall-font-size" style="margin-top: var(--wp--preset--spacing--10);" v-html="event.teaser"></p>

							<p class="has-tiny-font-size" style="margin-top: var(--wp--preset--spacing--20);">
								<strong v-html="labels.date_time"></strong><br/>
								<span v-html="event.date_range"></span>
							</p>

							<p class="has-tiny-font-size" style="margin-top: var(--wp--preset--spacing--20);" v-if="event.location">
								<strong v-html="labels.location"></strong><br/>
								<a class="event-link" v-if="event.location.url" :href="event.location.url" target="_blank" rel="noopener nofollow" v-html="event.location.name"></a>
								<span v-else v-html="event.location.name"></span>
								<template v-if="event.location.directions_url">
									<span>&nbsp;|&nbsp;<a class="event-link" :href="event.location.directions_url" target="_blank" rel="noopener nofollow" v-html="labels.directions"></a></span>
								</template>
							</p>

							<div class="wp-block-buttons" style="margin-top: var(--wp--preset--spacing--20);">
								<div class="wp-block-button is-style-outline">
									<a class="wp-block-button__link" :href="eventsStore.getEventUrl(event.key)" v-html="labels.learn_more" style="padding-top: var(--wp--preset--spacing--10); padding-bottom: var(--wp--preset--spacing--10); padding-left: var(--wp--preset--spacing--20); padding-right: var(--wp--preset--spacing--20); font-size: var(--wp--preset--font-size--xsmall);"></a>
								</div>
							</div>
						</div>
					</div>
				</li>
			</ul>
		</div>
	</div>
</template>
